import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { debounceTime, Subject } from 'rxjs';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { TipoProdutoEnum } from '../../../shared/enums/produto/tipo-produto.enum';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { MaskService } from '../../../shared/services/mask.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { OrdemProducaoOrquestradorService } from '../../../shared/services/apis/orquestrador-suprimentos/ordem-producao-orquestrador.service';
import { OrdemProducaoService } from '../../../shared/services/apis/suprimentos/ordem-producao.service';
import { OrdemProducaoItemStruct } from '../../../shared/services/structs/suprimentos/ordem-producao-item.struct';
import { OrdemProducaoEtapaStruct } from '../../../shared/services/structs/suprimentos/ordem-producao-etapa.struct';
import { TagProducaoStruct } from '../../../shared/services/structs/suprimentos/tag-producao.struct';
import { environment } from '../../../../environments/environment';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';

@Component({
  selector: 'app-ordem-producao-cadastro',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatDatepickerModule,
    MatTooltipModule,
    SelectPesquisavelComponent,
    LoadingBoxDefaultListComponent,
    NgxCurrencyDirective,
    FooterActionComponent,
    AbntPipe,
  ],
  templateUrl: './ordem-producao-cadastro.component.html',
  styleUrls: ['./ordem-producao-cadastro.component.css'],
})
export class OrdemProducaoCadastroComponent implements OnInit {
  public moduloEnum = ModuloEnum.Suprimentos;
  public funcionalidadeEnum = FuncionalidadeEnum.Suprimentos_OrdemProducao;

  public model!: FormGroup;
  public _isLoading = false;
  public _isSalvando = false;

  public idOrdemProducao: number | null = null;
  public get isEdicao(): boolean {
    return this.idOrdemProducao !== null;
  }

  /**
   * Lookup de produto. O filtro de tipo vai em extraParams e não embutido na URL — o
   * select-pesquisavel monta a query string, então concatenar aqui duplicaria o "?".
   *
   * Usamos select-pesquisavel e não autocomplete-pesquisavel de propósito: o autocomplete só
   * consulta a partir de 3 caracteres digitados e devolve lista vazia antes disso, ou seja, não
   * mostra nada ao abrir. O select consulta com texto vazio, então já abre com a lista.
   */
  public readonly urlProdutoLookup = `${environment.urlApiProduto}Lookup/listProdutoSelect`;

  /** Só produto Fabricado pode ser produzido — regra central da US (seção 4.2). */
  public readonly filtroProdutoFabricado = { listIdTipoProduto: TipoProdutoEnum.Fabricado, incluirSaldo: true };

  public composicao: OrdemProducaoItemStruct[] = [];
  public etapas: OrdemProducaoEtapaStruct[] = [];
  public saldoApurado = true;
  public unidadeProduto = '';
  public numeroOrdemProducao: number;

  public listaTags: TagProducaoStruct[] = [];

  /** Unidade do produto selecionado permite fração? Decide a máscara da quantidade (mesmo padrão
   * do produto-cadastro: unidade não fracionada força número inteiro). */
  public isFracionadaProduto = true;

  public get quantidadeMask() {
    return this.isFracionadaProduto ? this.maskService.getDynamicDecimalMask() : this.maskService.getIntegerMask();
  }

  public colunasComposicao: string[] = [];

  /** Evita uma chamada de composição por tecla digitada na quantidade. */
  private composicaoTrigger = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private ordemProducaoOrquestradorService: OrdemProducaoOrquestradorService,
    private ordemProducaoService: OrdemProducaoService,
    private alertService: AlertService,
    private maskService: MaskService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      idProduto: [null, Validators.required],
      quantidade: [null, [Validators.required, Validators.min(0.0001)]],
      dataInicio: [new Date(), Validators.required],
      dataPrevistaTermino: [null],
      observacoes: [''],
      listIdTagProducao: [[]],
    });

    this.atualizarColunasComposicao();
    this.carregarLookups();

    this.composicaoTrigger.pipe(debounceTime(400)).subscribe(() => this.carregarComposicao());

    // O currencyMask (ngx-currency) atualiza o FormControl direto, sem disparar o evento nativo
    // "change" do input — por isso reage por valueChanges, mesmo padrão do produto-producao.
    this.model.get('quantidade')?.valueChanges.subscribe(() => this.composicaoTrigger.next());

    const idParam = this.route.snapshot.paramMap.get('idOrdemProducao');
    if (idParam) {
      this.idOrdemProducao = Number(idParam);
      this.carregarOrdem();
      return;
    }

    // Clonar (seção 8.1 item 5): abre como Nova OP pré-preenchida a partir de outra. idioma de
    // passar estado pela navegação já usado em inventario-digitacao/conciliacao-bancaria-contas.
    const clonarDeIdOrdemProducao = (window.history.state as { clonarDeIdOrdemProducao?: number })
      ?.clonarDeIdOrdemProducao;
    if (clonarDeIdOrdemProducao) {
      this.carregarClone(clonarDeIdOrdemProducao);
    }
  }

  /**
   * Pré-preenche com produto/quantidade/tags/observações e a data prevista da OP de origem — a
   * data é redefinida para hoje (já é o default do form) e as etapas nascem zeradas, porque
   * idProduto/quantidade disparam o recálculo normal de composição via composicaoTrigger.
   */
  private carregarClone(idOrdemProducaoOrigem: number): void {
    this._isLoading = true;
    this.ordemProducaoOrquestradorService.getById(idOrdemProducaoOrigem).subscribe({
      next: (res) => {
        this._isLoading = false;

        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        const origem = res.ordemProducao;
        this.model.patchValue({
          idProduto: origem.idProduto,
          quantidade: origem.quantidade,
          dataPrevistaTermino: origem.dataPrevistaTermino,
          observacoes: origem.observacoes ?? '',
          listIdTagProducao: (origem.tags ?? []).map((t) => t.idTagProducao),
        });
      },
      error: (err) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error);
      },
    });
  }

  private atualizarColunasComposicao(): void {
    // Sem saldo apurado a coluna sai da tabela — mostrar 0 leria como falta de estoque.
    this.colunasComposicao = this.saldoApurado
      ? ['nome', 'sku', 'quantidadeUnitaria', 'quantidadeTotal', 'saldo']
      : ['nome', 'sku', 'quantidadeUnitaria', 'quantidadeTotal'];
  }

  private carregarLookups(): void {
    this.ordemProducaoService.getLookup().subscribe({
      next: (res) => {
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }
        this.listaTags = res.tags ?? [];
      },
      error: (err) => this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error),
    });
  }

  private carregarOrdem(): void {
    if (this.idOrdemProducao === null) return;

    this._isLoading = true;
    this.ordemProducaoOrquestradorService.getById(this.idOrdemProducao).subscribe({
      next: (res) => {
        this._isLoading = false;

        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          return;
        }

        const ordem = res.ordemProducao;

        // Trava de edição (seção 4.8): OP com estoque lançado não abre o formulário.
        if (ordem.isEstoqueLancado) {
          this.alertService.show('Atenção', 'Exclua o estoque para editar.', AlertType.warning);
          this.router.navigate(['/suprimentos/ordem-producao']);
          return;
        }

        this.numeroOrdemProducao = ordem.numero;
        this.unidadeProduto = ordem.unidade;
        this.isFracionadaProduto = ordem.isUnidadeMedidaFracionada;

        this.model.patchValue({
          idProduto: ordem.idProduto,
          quantidade: ordem.quantidade,
          dataInicio: ordem.dataInicio,
          dataPrevistaTermino: ordem.dataPrevistaTermino,
          observacoes: ordem.observacoes ?? '',
          listIdTagProducao: (ordem.tags ?? []).map((t) => t.idTagProducao),
        });

        this.composicao = ordem.itens ?? [];
        this.etapas = ordem.etapas ?? [];
        this.atualizarColunasComposicao();
      },
      error: (err) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error);
      },
    });
  }

  // ── Produto e composição ───────────────────────────────────────────────────

  /**
   * O select-pesquisavel escreve o id direto no control e emite o produto selecionado. Nome e SKU
   * vêm da resposta de composição (resolvidos no servidor); isUnidadeMedidaFracionada já vem aqui, para a
   * máscara da quantidade reagir na hora, sem esperar o debounce da composição.
   */
  public onProdutoAlterado(produto?: any): void {
    this.isFracionadaProduto = produto?.isUnidadeMedidaFracionada ?? true;
    this.composicaoTrigger.next();
  }

  /**
   * Busca composição e etapas no servidor. O preview e a gravação usam o mesmo cálculo — o
   * POST/PUT recalcula do zero, então o que aparece aqui não é reenviado.
   */
  private carregarComposicao(): void {
    const idProduto = this.model.get('idProduto')?.value;
    const quantidade = this.model.get('quantidade')?.value;

    if (!idProduto || !quantidade || quantidade <= 0) {
      this.composicao = [];
      this.etapas = [];
      return;
    }

    this._isLoading = true;
    this.ordemProducaoOrquestradorService.getComposicao(idProduto, quantidade).subscribe({
      next: (res) => {
        this._isLoading = false;

        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.composicao = [];
          this.etapas = [];
          return;
        }

        this.composicao = res.itens ?? [];
        this.etapas = res.etapas ?? [];
        this.saldoApurado = res.saldoApurado;
        this.unidadeProduto = res.unidade || this.unidadeProduto;
        this.isFracionadaProduto = res.isUnidadeMedidaFracionada;
        this.atualizarColunasComposicao();
      },
      error: (err) => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error);
      },
    });
  }

  /** Vermelho quando o saldo não cobre a necessidade (seção 4.5). */
  public saldoInsuficiente(item: OrdemProducaoItemStruct): boolean {
    return item.saldoAtual !== null && item.saldoAtual < item.quantidadeTotal;
  }

  public get temComposicao(): boolean {
    return this.composicao.length > 0;
  }

  public get produtoInvalido(): boolean {
    const control = this.model.get('idProduto');
    return !!control && !control.value && control.touched;
  }

  public onDateInput(event: Event, controlName: string): void {
    MaskService.onDateInput(event, this.model.get(controlName));
  }

  // ── Tags ───────────────────────────────────────────────────────────────────

  public get tagsSelecionadas(): TagProducaoStruct[] {
    const ids: number[] = this.model.get('listIdTagProducao')?.value ?? [];
    return this.listaTags.filter((t) => ids.includes(t.idTagProducao));
  }

  public removerTag(idTagProducao: number): void {
    const ids: number[] = this.model.get('listIdTagProducao')?.value ?? [];
    this.model.patchValue({ listIdTagProducao: ids.filter((id) => id !== idTagProducao) });
  }

  // ── Salvar / cancelar ──────────────────────────────────────────────────────

  public salvar(): void {
    if (this.model.invalid) {
      this.model.markAllAsTouched();
      this.alertService.show('Atenção', 'Preencha o produto e a quantidade.', AlertType.warning);
      return;
    }

    const request = {
      idProduto: this.model.get('idProduto')?.value,
      quantidade: this.model.get('quantidade')?.value,
      dataInicio: this.model.get('dataInicio')?.value,
      dataPrevistaTermino: this.model.get('dataPrevistaTermino')?.value,
      observacoes: this.model.get('observacoes')?.value ?? '',
      listIdTagProducao: this.model.get('listIdTagProducao')?.value ?? [],
    };

    this._isSalvando = true;

    if (this.isEdicao && this.idOrdemProducao !== null) {
      const idOrdemProducao = this.idOrdemProducao;
      this.ordemProducaoOrquestradorService.atualizar(idOrdemProducao, request).subscribe({
        next: (res) => this.tratarRetornoSalvar(res.isError, res.errorDescription, 'Ordem de produção atualizada.', idOrdemProducao),
        error: (err) => this.tratarErroSalvar(err),
      });
      return;
    }

    this.ordemProducaoOrquestradorService.criar(request).subscribe({
      next: (res) => this.tratarRetornoSalvar(res.isError, res.errorDescription, 'Ordem de produção criada.', res.idOrdemProducao),
      error: (err) => this.tratarErroSalvar(err),
    });
  }

  /** Ao salvar, navega para o Detalhe (seção 4.8 da US). */
  private tratarRetornoSalvar(isError: boolean, errorDescription: string, mensagemSucesso: string, idOrdemProducao: number): void {
    this._isSalvando = false;

    if (isError) {
      this.alertService.show('Erro', errorDescription, AlertType.error);
      return;
    }

    this.alertService.show('Sucesso', mensagemSucesso, AlertType.success);
    this.router.navigate(['/suprimentos/ordem-producao', idOrdemProducao, 'visualizar']);
  }

  private tratarErroSalvar(err: any): void {
    this._isSalvando = false;
    this.alertService.show('Erro inesperado', err.message ?? err, AlertType.error);
  }

  public cancelar(): void {
    this.router.navigate(['/suprimentos/ordem-producao']);
  }
}
